import { Dispatch, SetStateAction, useRef, useState } from "react";
import Tabs from "../../Tabs";
import { fileWrapper, folderWrapper } from "./NewFilesUploader";
import { Button } from "../../tailwind/Button";
import { DataRoomPermission } from "@/src/graphql/generated";
import { Option } from "../../tailwind/Dropdown";
import { DocumentPlusIcon } from "@heroicons/react/24/solid";
import { classNames } from "@/src/utils/cn";
import { LocalFileItem } from "./LocalFileItem";
import { LocalFolderItem } from "./LocalFolderItem";
import { FileUpIcon, FolderPlusIcon, TrashIcon } from "lucide-react";
import { useUploadContext } from "./useUploadContext";
import { v4 as uuid } from "uuid";

import {
  createFileWithNewName,
  getFilesFromDataTransferItem,
} from "./parseDataTransferFiles";

export { createFileWithNewName, getFilesFromDataTransferItem };

// Add this utility function for recursive folder updates
export function updateFolderRecursively(
  folders: folderWrapper[],
  targetFolderId: number,
  updater: (folder: folderWrapper) => folderWrapper,
): folderWrapper[] {
  return folders.map((folder) => {
    // Recursively update children if they exist
    if (folder.id === targetFolderId) {
      return updater(folder);
    }

    if (folder.children && folder.children.length > 0) {
      return {
        ...folder,
        children: updateFolderRecursively(
          folder.children,
          targetFolderId,
          updater,
        ),
      };
    }

    return folder;
  });
}

export function MultiStepUploader(props: {
  dataRoomFolderId?: string;
  questionId?: string;
  permissions: Pick<DataRoomPermission, "id" | "name" | "description">[];
  onDone: () => void;
  /** Keep the modal open to show inline progress rather than backgrounding. */
  closeOnSubmit?: boolean;
  parentHasIndex?: boolean;
}) {
  const {
    uploadFiles,
    uploadFolders,
    setUploadFiles,
    setUploadFolders,
    isUploading: uploading,
    startUploads,
    uploadFile,
    uploadFolder,
    clearQueue,
  } = useUploadContext();

  const tabs = [
    {
      label: "Upload",
      icon: <FileUpIcon className="w-4 h-4" />,
      content: (
        <UploadTab
          dataRoomFolderId={props.dataRoomFolderId || ""}
          permissions={props.permissions}
          files={uploadFiles}
          onFilesChange={setUploadFiles}
          uploading={uploading}
          onFoldersChange={setUploadFolders}
          folders={uploadFolders}
          uploadFile={(f) =>
            uploadFile(f, {
              dataRoomFolderId: props.dataRoomFolderId,
              questionId: props.questionId,
            })
          }
          uploadFolder={(f) =>
            uploadFolder(f, undefined, {
              dataRoomFolderId: props.dataRoomFolderId,
            })
          }
        />
      ),
      badge: (uploadFiles.length + uploadFolders.length).toString(),
    },
  ];

  return (
    <div className="flex-1 flex flex-col">
      <Tabs tabs={tabs} />
      <Footer
        disabled={uploadFiles.length === 0 && uploadFolders.length === 0}
        onClear={() => {
          clearQueue();
        }}
        onDone={() => {
          props.onDone();
        }}
        onRetry={() => {
          startUploads({
            dataRoomFolderId: props.dataRoomFolderId,
            questionId: props.questionId,
          });
        }}
        onSubmit={() => {
          startUploads({
            dataRoomFolderId: props.dataRoomFolderId,
            questionId: props.questionId,
            files: uploadFiles,
            folders: uploadFolders,
          });
          if (props.closeOnSubmit !== false) {
            // Data-room uploads continue in the route-scoped manager.
            props.onDone();
          }
        }}
        uploadFiles={uploadFiles}
        uploadFolders={uploadFolders}
      />
    </div>
  );
}

interface TabProps {
  uploading: boolean;
  onFilesChange: Dispatch<SetStateAction<fileWrapper[]>>;
  onFoldersChange: Dispatch<SetStateAction<folderWrapper[]>>;
  files: fileWrapper[];
  folders?: folderWrapper[];
  dataRoomFolderId?: string;
  permissions: Pick<DataRoomPermission, "id" | "name" | "description">[];
  uploadFile: (file: fileWrapper) => void;
  uploadFolder: (folderWrap: folderWrapper) => void;
}

function UploadTab(props: TabProps) {
  const [dragActive, setDragActive] = useState(false);

  const inputFolderRef = useRef<HTMLInputElement>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState("");

  const defaultPermission =
    props.permissions.length > 0 ? props.permissions[0] : null;
  const [selectedPermission] = useState<Option>({
    label: defaultPermission ? defaultPermission.name : "",
    value: defaultPermission ? defaultPermission.id : "",
    description: defaultPermission ? defaultPermission.description : "",
  });

  const allowFolders = props.folders;

  const handleDrag = function (e: any) {
    setFileError("");
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const addFileToTree = (
    file: File,
    folderTree: folderWrapper[],
    fileItems: fileWrapper[],
  ) => {
    const relativePath: string =
      file.webkitRelativePath || (file as any).fullPath || "";
    const fileName = file.name.replace(/\.[^/.]+$/, "");

    // Split the relative path into parts (filtering out empty strings)
    const parts = relativePath.split("/").filter(Boolean);

    // If there is at least one folder and one file name, treat it as nested.
    if (parts.length > 1) {
      const folderNames = parts.slice(0, parts.length - 1);
      let currentFolders = folderTree;
      let currentFolder: folderWrapper | undefined;

      folderNames.forEach((folderName) => {
        let folder = currentFolders.find((f) => f.name === folderName);
        if (!folder) {
          folder = {
            id: Math.random(),
            name: folderName,
            uploadProgress: 0,
            status: "pending",
            files: [],
            children: [],
            selectedPermission: {
              value: defaultPermission ? defaultPermission.id : "",
              label: defaultPermission ? defaultPermission.name : "",
              description: defaultPermission
                ? defaultPermission.description
                : "",
            },
          };
          currentFolders.push(folder);
        }
        currentFolder = folder;
        currentFolders = folder.children!;
      });

      // Only add the file if the file name is nonempty.
      if (fileName.trim() !== "" && currentFolder) {
        currentFolder.files = currentFolder.files || [];
        currentFolder.files.push({
          id: uuid(),
          file,
          name: fileName,
          dataRoomPermissionId: defaultPermission ? defaultPermission.id : "",
          uploadProgress: 0,
          status: "pending",
          parentFolder: currentFolder,
          selectedPermission: {
            value: defaultPermission ? defaultPermission.id : "",
            label: defaultPermission ? defaultPermission.name : "",
            description: defaultPermission ? defaultPermission.description : "",
          },
        });
      }
    } else {
      // For top-level files, add directly.
      fileItems.push({
        id: uuid(),
        file,
        name: fileName,
        dataRoomPermissionId: defaultPermission ? defaultPermission.id : "",
        uploadProgress: 0,
        status: "pending",
        selectedPermission: {
          value: defaultPermission ? defaultPermission.id : "",
          label: defaultPermission ? defaultPermission.name : "",
          description: defaultPermission ? defaultPermission.description : "",
        },
      });
    }
  };

  const handleDrop = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const fileItems: fileWrapper[] = [];
    const folderTree: folderWrapper[] = [];

    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      const promises = Array.from(
        e.dataTransfer.items as DataTransferItem[],
      ).map(async (item) => {
        const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
        if (!entry) return;
        if (entry.isDirectory) {
          const nestedFiles = await getFilesFromDataTransferItem(item);
          nestedFiles.forEach((f: File) => {
            let file = f;
            if (file.name.endsWith(".qbb.txt")) {
              const newName = file.name.replace(".qbb.txt", ".qbb");
              file = createFileWithNewName(file, newName);
            }
            addFileToTree(file, folderTree, fileItems);
          });
        } else if (entry.isFile) {
          let file = item.getAsFile();

          if (file) {
            if (file.name.endsWith(".qbb.txt")) {
              const newName = file.name.replace(".qbb.txt", ".qbb");
              file = createFileWithNewName(file, newName);
            }
            addFileToTree(file, folderTree, fileItems);
          }
        }
      });
      await Promise.all(promises);
    }
    props.onFilesChange([...props.files, ...fileItems]);
    if (props.onFoldersChange) {
      props.onFoldersChange([...(props.folders || []), ...folderTree]);
    }
  };

  if (props.files.length > 0 || (props.folders && props.folders.length > 0)) {
    return (
      <div
        className="px-4"
        key={props.files.length + (props.folders?.length || 0)}
      >
        <div className="max-h-[380px] overflow-y-auto scrollbar-thin">
          <div className="max-h-full overflow-y-auto">
            {props.onFoldersChange &&
              props.folders &&
              props.folders.length > 0 &&
              props.folders
                .sort((a, b) => {
                  if (a.status === "complete" && b.status !== "complete") {
                    return 1;
                  }
                  if (a.status !== "complete" && b.status === "complete") {
                    return -1;
                  }

                  const aMatch = a.name.match(/^(\d+)\./);
                  const bMatch = b.name.match(/^(\d+)\./);

                  if (aMatch && bMatch) {
                    // If both match the pattern, compare the parsed numbers
                    const aNum = parseInt(aMatch[1]);
                    const bNum = parseInt(bMatch[1]);
                    return aNum - bNum;
                  }

                  return a.name.localeCompare(b.name);
                })
                .map((file, i) => (
                  <LocalFolderItem
                    key={i}
                    folder={file}
                    onFilesChange={props.onFilesChange}
                    onFoldersChange={props.onFoldersChange}
                    permissions={props.permissions}
                    depth={0}
                  />
                ))}
            <div key={props.files.length}>
              {props.files
                .sort((a, b) => {
                  if (a.status === "complete" && b.status !== "complete") {
                    return 1;
                  }
                  if (a.status !== "complete" && b.status === "complete") {
                    return -1;
                  }

                  const aMatch = a.name.match(/^(\d+)\./);
                  const bMatch = b.name.match(/^(\d+)\./);

                  if (aMatch && bMatch) {
                    // If both match the pattern, compare the parsed numbers
                    const aNum = parseInt(aMatch[1]);
                    const bNum = parseInt(bMatch[1]);
                    return aNum - bNum;
                  }

                  return a.name.localeCompare(b.name);
                })
                .map((file, i) => (
                  <LocalFileItem
                    key={i}
                    file={file}
                    onFilesChange={props.onFilesChange}
                    onFoldersChange={props.onFoldersChange}
                    permissions={props.permissions}
                    uploadFile={props.uploadFile}
                  />
                ))}
            </div>
          </div>
        </div>
        <div>
          {!props.uploading ? (
            <div
              className={classNames(
                "p-2 my-2 flex-1 gap-x-2 bg-gray-50 border-2 transition-all duration-300 ease-in-out rounded-md border-dashed border-gray-300 flex items-center justify-between",
                dragActive ? "border-persian-500 bg-persian-100/80 shadow" : "",
                fileError ? "border-red-500" : "",
              )}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <p className="text-sm text-gray-500 font-medium self-center">
                Drop new documents {allowFolders ? "or folders" : ""} here
              </p>
              <div className="flex gap-x-2">
                {allowFolders && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-x-1 bg-white text-persian-500 border px-2 py-1 rounded-full font-semibold hover:border-persian-400 hover:shadow text-sm"
                    onClick={() => {
                      setFileError("");
                      if (inputFolderRef.current) {
                        inputFolderRef.current.click();
                      }
                    }}
                  >
                    <FolderPlusIcon className="w-5 h-5" />
                    <p>Upload folder</p>
                  </button>
                )}
                <button
                  type="button"
                  className="inline-flex items-center gap-x-1 bg-white text-persian-500 border px-2 py-1 rounded-full font-semibold hover:border-persian-400 hover:shadow text-sm"
                  onClick={() => {
                    setFileError("");
                    if (inputFileRef.current) {
                      inputFileRef.current.click();
                    }
                  }}
                >
                  <DocumentPlusIcon className="w-5 h-5" />
                  <p>Upload document</p>
                </button>
              </div>
            </div>
          ) : null}
          {allowFolders && (
            <FolderInput
              onFilesChange={props.onFilesChange}
              onFoldersChange={props.onFoldersChange}
              inputFolderRef={inputFolderRef}
              addFileToTree={addFileToTree}
            />
          )}
          <FileInput
            onFilesChange={props.onFilesChange}
            onFoldersChange={props.onFoldersChange}
            inputFileRef={inputFileRef}
            addFileToTree={addFileToTree}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 h-80">
      <div className="h-full flex flex-col">
        <div
          className={classNames(
            "p-2 flex-1 gap-x-2 bg-gray-50 border-2 transition-all duration-300 ease-in-out rounded-md border-dashed border-gray-300 flex flex-col items-center justify-center",
            dragActive ? "border-persian-500 bg-persian-100/80 shadow" : "",
            fileError ? "border-red-500" : "",
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <p className="text-sm mb-1 text-gray-500 font-medium text-center">
            Drop new documents {allowFolders ? "or folders" : ""} here
          </p>
          <div className="flex justify-center mt-5 items-center">
            <div className="flex gap-x-2">
              {allowFolders && (
                <button
                  type="button"
                  className="inline-flex items-center gap-x-1 bg-white text-persian-500 border px-2 py-1 rounded-full font-semibold hover:border-persian-400 hover:shadow text-sm"
                  onClick={() => {
                    setFileError("");
                    if (inputFolderRef.current) {
                      inputFolderRef.current.click();
                    }
                  }}
                >
                  <FolderPlusIcon className="w-5 h-5" />
                  <p>Upload folder</p>
                </button>
              )}
              <button
                type="button"
                className="inline-flex items-center gap-x-1 bg-white text-persian-500 border px-2 py-1 rounded-full font-semibold hover:border-persian-400 hover:shadow text-sm"
                onClick={() => {
                  setFileError("");
                  if (inputFileRef.current) {
                    inputFileRef.current.click();
                  }
                }}
              >
                <DocumentPlusIcon className="w-5 h-5" />
                <p>Upload document</p>
              </button>
            </div>

            <FileInput
              onFilesChange={props.onFilesChange}
              onFoldersChange={props.onFoldersChange}
              inputFileRef={inputFileRef}
              addFileToTree={addFileToTree}
            />
            {allowFolders && (
              <FolderInput
                onFilesChange={props.onFilesChange}
                onFoldersChange={props.onFoldersChange}
                inputFolderRef={inputFolderRef}
                addFileToTree={addFileToTree}
              />
            )}
          </div>
        </div>

        {fileError ? (
          <p className="text-red-500 font-medium mt-2 text-sm">{fileError}</p>
        ) : null}
      </div>
    </div>
  );
}

function FileInput(props: {
  onFilesChange: Dispatch<SetStateAction<fileWrapper[]>>;
  onFoldersChange?: Dispatch<SetStateAction<folderWrapper[]>>;
  inputFileRef: React.RefObject<HTMLInputElement>;
  addFileToTree: (
    file: File,
    folderTree: folderWrapper[],
    fileItems: fileWrapper[],
  ) => void;
}) {
  return (
    <div>
      <input
        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.files) {
            const fileItems: fileWrapper[] = [];
            const folderTree: folderWrapper[] = [];
            Array.from(e.target.files).forEach((file) => {
              if (file.name.endsWith(".qbb.txt")) {
                const newName = file.name.replace(".qbb.txt", ".qbb");
                file = createFileWithNewName(file, newName);
              }
              console.log(file);

              props.addFileToTree(file, folderTree, fileItems);
            });

            props.onFilesChange((prevFiles) => [...prevFiles, ...fileItems]);
            if (props.onFoldersChange) {
              props.onFoldersChange((prevFolders) => [
                ...prevFolders,
                ...folderTree,
              ]);
            }
          }
        }}
        ref={props.inputFileRef}
        type="file"
        multiple
        style={{ display: "none" }}
      />
    </div>
  );
}

function FolderInput(props: {
  onFilesChange: Dispatch<SetStateAction<fileWrapper[]>>;
  onFoldersChange?: Dispatch<SetStateAction<folderWrapper[]>>;
  inputFolderRef: React.RefObject<HTMLInputElement>;
  addFileToTree: (
    file: File,
    folderTree: folderWrapper[],
    fileItems: fileWrapper[],
  ) => void;
}) {
  return (
    <div>
      <input
        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.files) {
            const fileItems: fileWrapper[] = [];
            const folderTree: folderWrapper[] = [];
            Array.from(e.target.files).forEach((file) => {
              props.addFileToTree(file, folderTree, fileItems);
            });
            props.onFilesChange((prevFiles) => [...prevFiles, ...fileItems]);
            if (props.onFoldersChange) {
              props.onFoldersChange((prevFolders) => [
                ...prevFolders,
                ...folderTree,
              ]);
            }
          }
        }}
        ref={props.inputFolderRef}
        type="file"
        multiple
        style={{ display: "none" }}
        {...{ webkitdirectory: "true" }}
      />
    </div>
  );
}

// Helper to get all files from a folder recursively (including nested subfolders)
function getAllFilesFromFolder(folder: folderWrapper): fileWrapper[] {
  const files: fileWrapper[] = [...(folder.files ?? [])];
  for (const child of folder.children ?? []) {
    files.push(...getAllFilesFromFolder(child));
  }
  return files;
}

// Helper to get all files from all folders
function getAllFilesFromFolders(folders: folderWrapper[]): fileWrapper[] {
  return folders.flatMap(getAllFilesFromFolder);
}

// Check if all files in a folder (and its subfolders) are complete
function allFilesInFolderComplete(folder: folderWrapper): boolean {
  const allFiles = getAllFilesFromFolder(folder);
  if (allFiles.length === 0) return folder.status === "complete";
  return allFiles.every(
    (f) => f.status === "complete" || f.createdDataRoomFileId,
  );
}

// Check if all files in all folders are complete
function allFilesInFoldersComplete(folders: folderWrapper[]): boolean {
  if (folders.length === 0) return false;
  return folders.every(allFilesInFolderComplete);
}

// Check if any file in any folder is uploading
function anyFileInFoldersUploading(folders: folderWrapper[]): boolean {
  const allFiles = getAllFilesFromFolders(folders);
  return allFiles.some((f) => f.status === "uploading");
}

function Footer(props: {
  onSubmit: () => void;
  onDone: () => void;
  onRetry: () => void;
  onClear: () => void;
  disabled: boolean;
  uploadFiles: fileWrapper[];
  uploadFolders: folderWrapper[];
}) {
  const standaloneFilesComplete =
    props.uploadFiles.length === 0 ||
    props.uploadFiles.every(
      (uf) => uf.status === "complete" || uf.createdDataRoomFileId,
    );
  const folderFilesComplete =
    props.uploadFolders.length === 0 ||
    allFilesInFoldersComplete(props.uploadFolders);

  const allComplete =
    props.uploadFiles.length + props.uploadFolders.length > 0 &&
    standaloneFilesComplete &&
    folderFilesComplete;

  const isUploading =
    props.uploadFiles.some((uf) => uf.status === "uploading") ||
    anyFileInFoldersUploading(props.uploadFolders);

  return (
    <div className="py-3 items-center justify-between flex px-3 border-t border-gray-200">
      <div className="flex items-center gap-x-2"></div>
      <div className="flex gap-x-2">
        {props.uploadFiles.some((uf) => uf.status === "error") ? (
          <Button
            variant="neutral"
            text="Retry failed files"
            onClick={props.onRetry}
          />
        ) : null}
        <Button
          variant="neutral"
          text="Clear all"
          icon={TrashIcon}
          isDisabled={
            props.uploadFiles.length + props.uploadFolders.length === 0
          }
          onClick={() => {
            props.onClear();
          }}
        />
        <Button
          variant="positive"
          text={allComplete ? "Done" : "Upload documents"}
          loadingText="Uploading documents..."
          isLoading={isUploading}
          isDisabled={props.disabled}
          onClick={() => {
            if (props.disabled) {
              return;
            }

            if (isUploading) {
              return;
            }

            if (allComplete) {
              props.onDone();
              return;
            }

            props.onSubmit();
          }}
        />
      </div>
    </div>
  );
}
